import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications as mantineNotifications } from '@mantine/notifications';
import { queryKeys } from '../lib/queryClient';
import * as notificationService from '../services/notificationService';
import type { ListQueryParams, NotificationPreferences } from '../types/api';

// Notifications list query
export const useNotifications = (params?: ListQueryParams & {
  unread?: boolean;
  type?: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}) => {
  return useQuery({
    queryKey: queryKeys.notifications.list(params),
    queryFn: () => notificationService.getNotifications(params),
    select: (data) => ({
      data: data.notifications || [],
      pagination: data.pagination,
      unreadCount: data.unread_count
    }),
    refetchInterval: 30000, // Refetch every 30 seconds
  });
};

// Single notification query
export const useNotification = (id: string) => {
  return useQuery({
    queryKey: queryKeys.notifications.detail(id),
    queryFn: () => notificationService.getNotification(id),
    enabled: !!id,
  });
};

// Notification preferences query
export const useNotificationPreferences = () => {
  return useQuery({
    queryKey: queryKeys.notifications.preferences(),
    queryFn: () => notificationService.getNotificationPreferences(),
  });
};

// Mark notification as read mutation
export const useMarkNotificationAsRead = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => notificationService.markNotificationAsRead(id),
    onSuccess: (_, notificationId) => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.notifications.lists() 
      });
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.notifications.detail(notificationId) 
      });
    },
    onError: (error: Error) => {
      mantineNotifications.show({
        title: 'Error',
        message: error.message || 'Failed to mark notification as read',
        color: 'red',
      });
    },
  });
};

// Mark all notifications as read mutation
export const useMarkAllNotificationsAsRead = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => notificationService.markAllNotificationsAsRead(),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.notifications.lists() 
      });
      mantineNotifications.show({
        title: 'Success',
        message: 'All notifications marked as read',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      mantineNotifications.show({
        title: 'Error',
        message: error.message || 'Failed to mark all notifications as read',
        color: 'red',
      });
    },
  });
};

// Delete notification mutation
export const useDeleteNotification = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => notificationService.deleteNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.notifications.lists() 
      });
      mantineNotifications.show({
        title: 'Success',
        message: 'Notification deleted successfully',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      mantineNotifications.show({
        title: 'Error',
        message: error.message || 'Failed to delete notification',
        color: 'red',
      });
    },
  });
};

// Clear all notifications mutation
export const useClearAllNotifications = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: () => notificationService.clearAllNotifications(),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.notifications.lists() 
      });
      mantineNotifications.show({
        title: 'Success',
        message: 'All notifications cleared',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      mantineNotifications.show({
        title: 'Error',
        message: error.message || 'Failed to clear notifications',
        color: 'red',
      });
    },
  });
};

// Update notification preferences mutation
export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (preferences: Partial<NotificationPreferences>) => 
      notificationService.updateNotificationPreferences(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({ 
        queryKey: queryKeys.notifications.preferences() 
      });
      mantineNotifications.show({
        title: 'Success',
        message: 'Notification preferences updated',
        color: 'green',
      });
    },
    onError: (error: Error) => {
      mantineNotifications.show({
        title: 'Error',
        message: error.message || 'Failed to update preferences',
        color: 'red',
      });
    },
  });
};